import type { Currency, CurrencyDef, Dataset, FolderId, MonthId } from "../domain/types.ts";
import { migrate } from "../store/migrations.ts";
import { monthOf } from "../domain/months.ts";
import { toDayOrdinal } from "../domain/days.ts";
import { chargeDateNeedsDay, startDateNeedsDay } from "../domain/granularity.ts";

export class ImportValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImportValidationError";
  }
}

export function exportDatasetJson(dataset: Dataset): string {
  return JSON.stringify(dataset, null, 2);
}

export function describeDataset(dataset: Dataset) {
  return {
    posts: dataset.posts.length,
    months: dataset.months.length,
    purchases: dataset.purchases.length,
  };
}

export function exportFilename(monthId: MonthId): string {
  return `budget-${monthId}.json`;
}

// Shape AND range: months 01-12 only. A shape-only regex (\d{2}) lets
// "2026-13" through import and it then silently normalizes to a different
// month inside the balance fold (the same defect fixed earlier in
// src/domain/months.ts). This is the import path that made it reachable.
const MONTH_ID = /^\d{4}-(0[1-9]|1[0-2])$/;
/**
 * A purchase date is "YYYY-MM-DD", or "YYYY-MM" when it has no specific day.
 * Validated here so a malformed date is rejected at the boundary rather than
 * throwing out of `monthOf()` deep inside the balance fold.
 */
const PURCHASE_DATE = /^\d{4}-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?$/;
/**
 * The day-granular subset of `PURCHASE_DATE`: "YYYY-MM-DD" only, no month-only
 * form. WHICH values need this narrower shape is `startDateNeedsDay` /
 * `chargeDateNeedsDay` in `src/domain/granularity.ts`, not a rule this file
 * restates: `everyNDays` and `everyNWeeks` hand `startDate` straight to
 * `addDays`, `everyNMonths` anchored to `lastCharge` hands it to
 * `addMonthsToDate`, and an anchored cost's CHARGE dates are stepped from the
 * same way (`src/domain/occurrences.ts`). All of those throw on a month-only
 * date, so it is refused here rather than three months later inside a fold.
 */
const DAY_GRANULAR_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const ANCHORINGS = new Set(["calendar", "lastCharge"]);
const RECURRENCE_KINDS = new Set(["everyNMonths", "everyNDays", "everyNWeeks"]);
const SPLIT_MODES = new Set(["percent", "fixed"]);

/**
 * `Currency` is an open string now, so this — not the type system — is what
 * stops a code the dataset never defined reaching the fold, where it would
 * throw MissingRateError or round at a guessed number of decimals.
 */
function requireCurrency(
  value: unknown,
  where: string,
  defined: ReadonlySet<Currency>,
): Currency {
  if (typeof value !== "string" || !defined.has(value)) {
    throw new ImportValidationError(
      `Unsupported currency ${String(value)} in ${where}. Define it under Settings first.`,
    );
  }
  return value;
}

/** Codes are identity: they key the FX table and every stored Money. */
const CURRENCY_CODE = /^[A-Z]{2,8}$/;

/**
 * Validates the currency table itself, before anything is checked against it.
 * A duplicate code would make two definitions compete for the same identity —
 * the code keys the FX table and every stored Money.
 *
 * Decimal places are NOT checked here any more: they are one dataset-wide
 * setting, validated below. A per-currency `digits` surviving in a hand-edited
 * file is dropped rather than refused — an unknown field is no reason to turn
 * away the owner's only backup.
 */
function requireCurrencyTable(data: Record<string, unknown>): CurrencyDef[] {
  const raw = data.currencies;
  if (!Array.isArray(raw)) {
    throw new ImportValidationError('Dataset field "currencies" is missing or not an array');
  }
  if (raw.length === 0) {
    throw new ImportValidationError("A dataset must define at least one currency");
  }

  const seen = new Set<string>();
  const table: CurrencyDef[] = [];

  for (const entry of raw as Array<Record<string, unknown>>) {
    const code = entry?.code;
    if (typeof code !== "string" || !CURRENCY_CODE.test(code)) {
      throw new ImportValidationError(
        `Currency code ${JSON.stringify(code)} is not 2-8 uppercase letters`,
      );
    }
    if (seen.has(code)) {
      throw new ImportValidationError(
        `Currency "${code}" is defined twice; which decimals apply would be arbitrary`,
      );
    }
    seen.add(code);

    if (entry.symbol !== undefined && typeof entry.symbol !== "string") {
      throw new ImportValidationError(`Currency "${code}" has a non-string symbol`);
    }
    if (entry.name !== undefined && typeof entry.name !== "string") {
      throw new ImportValidationError(`Currency "${code}" has a non-string name`);
    }

    table.push({
      code,
      ...(typeof entry.symbol === "string" ? { symbol: entry.symbol } : {}),
      ...(typeof entry.name === "string" ? { name: entry.name } : {}),
    });
  }

  return table;
}

function requireArray(data: Record<string, unknown>, key: string): unknown[] {
  const value = data[key];
  if (!Array.isArray(value)) {
    throw new ImportValidationError(`Dataset field "${key}" is missing or not an array`);
  }
  return value;
}

function requireFolderTree(data: Dataset): void {
  const folders = requireArray(data as unknown as Record<string, unknown>, "folders");
  const parents = new Map<FolderId, FolderId | null>();
  const siblingOrders = new Map<FolderId | null, Set<number>>();
  const nestedBillOrders = new Map<string, Set<number>>();
  const requireOrder = (order: unknown, label: string) => {
    if (typeof order !== "number" || !Number.isFinite(order) || !Number.isInteger(order)) {
      throw new ImportValidationError(`${label} has an invalid order`);
    }
  };
  const requireLocation = (folderId: unknown, order: unknown, label: string) => {
    if (folderId !== null && (typeof folderId !== "string" || !parents.has(folderId))) {
      throw new ImportValidationError(`${label} references unknown folder "${String(folderId)}"`);
    }
    requireOrder(order, label);
    const key = folderId as FolderId | null;
    const orders = siblingOrders.get(key) ?? new Set<number>();
    if (orders.has(order as number)) {
      throw new ImportValidationError(`${label} duplicates sibling order ${String(order)}`);
    }
    orders.add(order as number);
    siblingOrders.set(key, orders);
  };

  for (const entry of folders) {
    const folder = entry as Record<string, unknown> | null;
    if (!folder || typeof folder.id !== "string" || !folder.id.trim()) {
      throw new ImportValidationError("Folder has an invalid id");
    }
    if (parents.has(folder.id)) {
      throw new ImportValidationError(`Duplicate folder id "${folder.id}"`);
    }
    if (typeof folder.name !== "string" || !folder.name.trim()) {
      throw new ImportValidationError(`Folder "${folder.id}" has an empty name`);
    }
    if (folder.parentId !== null && typeof folder.parentId !== "string") {
      throw new ImportValidationError(`Folder "${folder.id}" has an invalid parent`);
    }
    parents.set(folder.id, folder.parentId as FolderId | null);
  }

  for (const [id, parentId] of parents) {
    if (parentId !== null && !parents.has(parentId)) {
      throw new ImportValidationError(`Folder "${id}" references unknown parent "${parentId}"`);
    }
    const visited = new Set<FolderId>();
    let cursor: FolderId | null = id;
    while (cursor !== null) {
      if (visited.has(cursor)) throw new ImportValidationError(`Folder cycle at "${cursor}"`);
      visited.add(cursor);
      cursor = parents.get(cursor) ?? null;
    }
  }

  for (const entry of folders) {
    const folder = entry as Record<string, unknown>;
    requireLocation(folder.parentId, folder.order, `Folder "${folder.id}"`);
  }
  for (const post of data.posts) {
    if (typeof post?.name !== "string") {
      throw new ImportValidationError("Post name must be a string");
    }
    requireLocation(post?.folderId, post?.order, `Post "${post?.name}"`);
  }
  for (const cost of data.recurring) {
    if (typeof cost?.name !== "string") {
      throw new ImportValidationError("Recurring cost name must be a string");
    }
    if (cost.parentPostId !== undefined) {
      if (!data.posts.some(post => post.id === cost.parentPostId)) throw new ImportValidationError(`Recurring cost "${cost.name}" references unknown parent post`);
      if (cost.folderId !== null && !parents.has(cost.folderId)) throw new ImportValidationError(`Recurring cost "${cost.name}" references unknown folder`);
      requireOrder(cost.order, `Recurring cost "${cost.name}"`);
      const orders = nestedBillOrders.get(cost.parentPostId) ?? new Set<number>();
      if (orders.has(cost.order)) throw new ImportValidationError(`Recurring cost "${cost.name}" duplicates nested order ${cost.order}`);
      orders.add(cost.order);
      nestedBillOrders.set(cost.parentPostId, orders);
    } else requireLocation(cost?.folderId, cost?.order, `Recurring cost "${cost?.name}"`);
  }
  for (const purchase of data.purchases) {
    if (purchase.placement !== undefined) {
      if (!purchase.placement || typeof purchase.placement !== "object") throw new ImportValidationError(`Purchase "${purchase.description}" has invalid placement`);
      requireLocation(purchase.placement.folderId, purchase.placement.order, `Purchase "${purchase.description}"`);
    }
  }
}

/**
 * `n` is checked hard rather than clamped. The projection walk in
 * `domain/occurrences.ts` terminates only if every step strictly advances, and
 * a clamped zero would silently give the owner a schedule their file does not
 * describe. Rejecting tells them the file is wrong; clamping tells them
 * nothing and quietly changes a bill.
 */
function requireRecurrence(recurrence: any, label: string): void {
  if (!recurrence || !RECURRENCE_KINDS.has(recurrence.kind)) {
    throw new ImportValidationError(
      `${label} has a recurrence of unknown kind "${String(recurrence?.kind)}"`,
    );
  }
  if (typeof recurrence.n !== "number" || !Number.isInteger(recurrence.n)) {
    throw new ImportValidationError(
      `${label} has a recurrence interval that is not a whole number: ${String(recurrence.n)}`,
    );
  }
  if (recurrence.n < 1) {
    throw new ImportValidationError(
      `${label} has a recurrence interval of ${recurrence.n}; it must be at least 1`,
    );
  }
  if (recurrence.kind === "everyNWeeks") {
    const { weekday } = recurrence;
    if (typeof weekday !== "number" || !Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
      throw new ImportValidationError(
        `${label} has a weekday of ${String(weekday)}; it must be 0-6, where 0 is Sunday`,
      );
    }
  }
}

/**
 * Shared by the purchases loop and the recurring-costs loop below — the only
 * two places a `splitMode`/`splits` pair occurs, and both feed the identical
 * `distributeByWeight`/`distributeByAmount` branch in `domain/charges.ts`.
 * One helper means the two write paths cannot silently drift into checking
 * to different standards; the two loops used to each carry their own copy of
 * the "one absorber" / "unknown post" checks, so this replaces both rather
 * than adding a third copy. Bills require a split; purchases may be unassigned.
 *
 * `splitMode` must be one of the two known modes: `charges.ts` picks its
 * distribution function with `splitMode === "percent" ? ... : ...`, so any
 * OTHER string silently takes the "fixed" branch and treats an intended
 * percentage as an absolute amount — a silently wrong number, not a throw.
 *
 * Every split's `value` must be a finite number. A non-finite value (missing,
 * a string, NaN, Infinity) would otherwise propagate through
 * `distributeByWeight`/`distributeByAmount` as NaN, and `roundMoney(NaN,
 * digits)` returns NaN rather than throwing — it would corrupt a total
 * silently, deep inside the fold.
 *
 * NEGATIVE values, values over 100, and splits that do not sum to the total
 * are all deliberately UNCHECKED — AGENTS.md lists these as intentional
 * (a refund is a normal line, and the absorbing split reconciles the sum by
 * design). Only non-numbers are rejected here.
 */
function requireSplits(
  splits: unknown,
  splitMode: unknown,
  label: string,
  postIds: ReadonlySet<string>,
  allowEmpty = false,
): void {
  if (!SPLIT_MODES.has(splitMode as string)) {
    throw new ImportValidationError(
      `${label} has an unknown splitMode "${String(splitMode)}"`,
    );
  }
  if (!Array.isArray(splits) || (splits.length === 0 && !allowEmpty)) {
    throw new ImportValidationError(`${label} has no splits; at least one split is required`);
  }
  if (splits.length === 0) return;
  const absorbers = (splits as Array<Record<string, unknown>>).filter(
    (s) => s?.absorbsRemainder,
  ).length;
  if (absorbers !== 1) {
    throw new ImportValidationError(
      `${label} has ${absorbers} splits flagged absorbsRemainder; exactly one is required`,
    );
  }
  for (const split of splits as Array<Record<string, unknown>>) {
    if (!postIds.has(split?.postId as string)) {
      throw new ImportValidationError(`${label} references unknown post "${String(split?.postId)}"`);
    }
    if (typeof split?.value !== "number" || !Number.isFinite(split.value)) {
      throw new ImportValidationError(
        `${label} has a split value that is not a finite number: ${String(split?.value)}`,
      );
    }
  }
}

/**
 * Validates far enough that the fold cannot throw on the imported data: every
 * split points at a real post, every assigned purchase has exactly one remainder
 * absorber, every MonthId parses, every rule version resolves unambiguously
 * to a number rather than to NaN or a missing rate, and every date the
 * projection walk will STEP FROM is both granular enough for the schedule
 * that reads it and calendar-valid (no "2026-09-31"), since shape and range
 * alone let an impossible date through. That last one covers a recurring
 * cost's own `startDate` and, for an anchored (`lastCharge`) cost, the `date`
 * of every purchase charged to it — an anchored cost has no slots, so its
 * charges' own dates are the anchors, and a month-only one there used to
 * import clean and take down every money route on the next fold.
 *
 * Note the order this runs in: `migrate` first, so what is validated is the
 * upgraded dataset. A file the migration produces must therefore satisfy
 * every rule below — see the 10 -> 11 step, which expands exactly the
 * month-only charge dates this function refuses.
 */
export function parseDatasetJson(text: string): Dataset {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    throw new ImportValidationError(
      `File is not valid JSON: ${(error as Error).message}`,
    );
  }

  let dataset: Dataset;
  try {
    dataset = migrate(raw);
  } catch (error) {
    throw new ImportValidationError((error as Error).message);
  }

  const data = dataset as unknown as Record<string, unknown>;
  const currencies = requireCurrencyTable(data);
  // The validated table replaces the raw one, so what comes back is normalised:
  // trimmed symbols and names, and no stray per-currency `digits` left over
  // from a hand-edited or pre-v6 file.
  dataset.currencies = currencies;
  const defined = new Set(currencies.map((currency) => currency.code));
  requireArray(data, "posts");
  requireArray(data, "months");
  requireArray(data, "purchases");
  requireArray(data, "fxRates");

  requireCurrency(dataset.settings.baseCurrency, "settings.baseCurrency", defined);
  if (dataset.settings.allowExistingRecurringCost !== undefined &&
      typeof dataset.settings.allowExistingRecurringCost !== "boolean") {
    throw new ImportValidationError("settings.allowExistingRecurringCost must be a boolean");
  }
  // One setting, every currency. Bad digits here would round every amount in
  // the dataset to nonsense, so this is checked before anything is folded.
  const digits = dataset.settings.digits;
  if (typeof digits !== "number" || !Number.isInteger(digits) || digits < 0 || digits > 4) {
    throw new ImportValidationError(
      `settings.digits is ${String(digits)}; expected a whole number of decimal places between 0 and 4`,
    );
  }
  if (!MONTH_ID.test(dataset.settings.foldStartMonth)) {
    throw new ImportValidationError(
      `Invalid foldStartMonth "${dataset.settings.foldStartMonth}"`,
    );
  }

  const postIds = new Set(dataset.posts.map((p) => p.id));
  for (const post of dataset.posts) {
    requireCurrency(post.currency, `post "${post.name}"`, defined);

    const rules = (post as unknown as Record<string, unknown>).rules;
    if (!Array.isArray(rules)) {
      throw new ImportValidationError(`Post "${post.name}" is missing its rules array`);
    }
    const seenFrom = new Set<string>();
    for (const version of rules as Array<Record<string, unknown>>) {
      const from = String(version.from);
      if (!MONTH_ID.test(from)) {
        throw new ImportValidationError(
          `Post "${post.name}" has a rule with an invalid start month "${from}"`,
        );
      }
      if (seenFrom.has(from)) {
        throw new ImportValidationError(
          `Post "${post.name}" has two rules starting in "${from}"; the effective rule would be ambiguous`,
        );
      }
      seenFrom.add(from);

      // Rule values may be negative or over 100, but must be finite numbers.
      // A missing or non-numeric value can silently turn the fold into NaN.
      const rule = (version.rule ?? {}) as {
        kind?: unknown;
        amount?: { amount?: unknown; currency?: unknown };
        percent?: unknown;
      };
      if (rule.kind === "fixed") {
        requireCurrency(rule.amount?.currency, `post "${post.name}" rule from ${from}`, defined);
        if (typeof rule.amount?.amount !== "number" || !Number.isFinite(rule.amount.amount)) {
          throw new ImportValidationError(
            `Post "${post.name}" has a fixed rule from "${from}" with an amount that is not a finite number`,
          );
        }
      } else if (rule.kind === "percentOfIncome") {
        if (typeof rule.percent !== "number" || !Number.isFinite(rule.percent)) {
          throw new ImportValidationError(
            `Post "${post.name}" has a percentage rule from "${from}" that is not a finite number`,
          );
        }
      } else {
        throw new ImportValidationError(
          `Post "${post.name}" has a rule from "${from}" of unknown kind "${String(rule.kind)}"`,
        );
      }
    }
  }

  const recurring = requireArray(data, "recurring") as any[];
  dataset.recurring = recurring;
  requireFolderTree(dataset);

  for (const cost of recurring) {
    if (!cost || typeof cost !== "object") {
      throw new ImportValidationError(
        `A recurring cost is missing or not an object: ${JSON.stringify(cost)}`,
      );
    }
    const label = `Recurring cost "${cost.name}"`;
    requireCurrency(cost.amount?.currency, label, defined);

    // Validated before the startDate/granularity checks below, because the
    // granularity rule for `everyNMonths` now depends on which anchoring the
    // cost carries — read it here rather than deferring, so a malformed
    // anchoring string does not silently read as "calendar" a few lines down.
    if (!ANCHORINGS.has(cost.anchoring)) {
      throw new ImportValidationError(
        `${label} has an unknown anchoring "${String(cost.anchoring)}"`,
      );
    }

    if (!PURCHASE_DATE.test(cost.startDate)) {
      throw new ImportValidationError(`${label} has an invalid start date "${cost.startDate}"`);
    }
    if (cost.endedFrom !== undefined && !PURCHASE_DATE.test(cost.endedFrom)) {
      throw new ImportValidationError(`${label} has an invalid ended-from date "${cost.endedFrom}"`);
    }
    requireRecurrence(cost.recurrence, label);
    // `everyNMonths` walks through `monthOf` under `calendar` anchoring, which
    // accepts either granularity — rent occurs IN a month. Under `lastCharge`
    // the same kind is computed day-precisely from a real charge date
    // (`stepFrom`'s `addMonthsToDate` branch), so it needs a real day too.
    // `everyNDays`/`everyNWeeks` reach `addDays` regardless of anchoring and
    // always need a day-granular startDate. `endedFrom` needs no such check —
    // it is only ever compared lexicographically, never fed to `addDays`.
    const dayGranular = DAY_GRANULAR_DATE.test(cost.startDate);
    if (startDateNeedsDay(cost.recurrence.kind, cost.anchoring) && !dayGranular) {
      throw new ImportValidationError(
        `${label} has a "${cost.recurrence.kind}" recurrence anchored to "${cost.anchoring}" with a ` +
          `month-only start date "${cost.startDate}"; it needs a day-granular startDate ("YYYY-MM-DD")`,
      );
    }
    // Shape and range (checked above by PURCHASE_DATE/DAY_GRANULAR_DATE) still
    // let a calendar-impossible date through, e.g. "2026-09-31" (September has
    // 30 days) or "2026-02-30". Route it through the same domain parsers the
    // fold itself uses, so an impossible date is refused here instead of
    // throwing three months later out of `foldBalances`.
    try {
      if (dayGranular) toDayOrdinal(cost.startDate);
      else monthOf(cost.startDate);
    } catch (error) {
      throw new ImportValidationError(
        `${label} has a calendar-impossible start date "${cost.startDate}" (${(error as Error).message})`,
      );
    }

    // `endedFrom` carries no granularity rule — checked above by not
    // requiring it to match the recurrence kind's day-granularity — but it
    // still has to be a real calendar date, the same way `startDate` does.
    if (cost.endedFrom !== undefined) {
      try {
        if (DAY_GRANULAR_DATE.test(cost.endedFrom)) toDayOrdinal(cost.endedFrom);
        else monthOf(cost.endedFrom);
      } catch (error) {
        throw new ImportValidationError(
          `${label} has a calendar-impossible ended-from date "${cost.endedFrom}" (${(error as Error).message})`,
        );
      }
    }

    requireSplits(cost.splits, cost.splitMode, label, postIds, true);
  }

  for (const month of dataset.months) {
    if (!MONTH_ID.test(month.id)) {
      throw new ImportValidationError(`Invalid month id "${month.id}"`);
    }
    requireCurrency(month.income.currency, `month ${month.id} income`, defined);
  }

  for (const purchase of dataset.purchases) {
    const label = `purchase "${purchase.description}"`;
    requireCurrency(purchase.total.currency, label, defined);

    if (!PURCHASE_DATE.test(purchase.date)) {
      throw new ImportValidationError(`${label} has an invalid date "${purchase.date}"`);
    }
    requireSplits(purchase.splits, purchase.splitMode, label, postIds, true);
    for (const slice of purchase.schedule?.slices ?? []) {
      if (!MONTH_ID.test(slice.month)) {
        throw new ImportValidationError(`${label} has invalid slice month "${slice.month}"`);
      }
      requireCurrency(slice.amount.currency, `${label} slice ${slice.month}`, defined);
    }

    if (purchase.source !== undefined) {
      // `null` is not `undefined`, so it reaches here without being "no
      // source". The field is typed as absent-or-object, never null, so a
      // null (or any other non-object) is malformed input, not a missing
      // field — reject it cleanly rather than dereferencing it into a raw
      // TypeError. `schedule?.slices ?? []` two blocks up is the same
      // defensive style applied to a sibling field that IS legitimately null.
      if (!purchase.source || typeof purchase.source !== "object") {
        throw new ImportValidationError(`${label} has a source that is not an object`);
      }
      // Captured into a local: narrowing `purchase.source` above does not
      // survive into the `find` callback below, which TypeScript treats as a
      // fresh closure over a possibly-reassigned property.
      const source = purchase.source;
      const cost = recurring.find((c: any) => c?.id === source.recurringId);
      if (!cost) {
        throw new ImportValidationError(
          `${label} names unknown recurring cost "${source.recurringId}"`,
        );
      }
      // A calendar cost has a grid, and a charge against it settles one slot,
      // so it must name that slot. An anchored (lastCharge) cost has no grid
      // at all — its next due date comes from the charge's OWN date, not from
      // any slot — so an occurrence date there describes a schedule this app
      // will not compute; refuse it rather than silently ignore it.
      if (cost.anchoring === "lastCharge") {
        if (source.occurrenceDate !== undefined) {
          throw new ImportValidationError(
            `${label} carries an occurrence date, but "${cost.name}" is anchored to its last ` +
              `charge and has no slots to settle`,
          );
        }
      } else {
        // Split from the "malformed" case below: `String(undefined)` prints
        // the literal word "undefined", which sends someone restoring a
        // backup searching their file for text that appears nowhere in it.
        // Missing and malformed are different problems and need different
        // messages, and this one names the cost the way the anchored-side
        // message above already does.
        if (source.occurrenceDate === undefined) {
          throw new ImportValidationError(
            `${label} has no occurrence date, but "${cost.name}" is calendar-anchored and a charge ` +
              `against it settles one slot`,
          );
        }
        if (!PURCHASE_DATE.test(source.occurrenceDate)) {
          throw new ImportValidationError(
            `${label} has an invalid occurrence date "${source.occurrenceDate}"`,
          );
        }
      }

      // An anchored cost has no slots, so this purchase's OWN date is the
      // anchor `anchoredOccurrences` steps forward from — under every
      // recurrence kind, day-precisely. `PURCHASE_DATE` above allows
      // "YYYY-MM", which is right for an ordinary purchase and wrong here: a
      // month-only date would import clean and then throw out of the very
      // first fold, from a frame that names no cost. The cost's own
      // `startDate` has been checked this way since the anchored model
      // landed; its charges were the half nobody checked.
      if (chargeDateNeedsDay(cost.anchoring)) {
        if (!DAY_GRANULAR_DATE.test(purchase.date)) {
          throw new ImportValidationError(
            `${label} is a charge for "${cost.name}", which is anchored to its last charge, so its ` +
              `date is what the next charge is measured from; it needs a day-granular date ` +
              `("YYYY-MM-DD"), not "${purchase.date}"`,
          );
        }
        // Day-shaped is not day-valid — same reason `startDate` is routed
        // through the domain's own parser above.
        try {
          toDayOrdinal(purchase.date);
        } catch (error) {
          throw new ImportValidationError(
            `${label} is a charge for "${cost.name}" and has a calendar-impossible date ` +
              `"${purchase.date}" (${(error as Error).message})`,
          );
        }
      }
    }
  }

  return dataset;
}
